/**
 * Hero Slider - Enhanced version based on hero-image section
 * Handles slide transitions, autoplay, and responsive behavior
 */

class HeroSlider {
  constructor(element) {
    this.slider = element;
    this.container = this.slider.querySelector('.hero-slider__container');
    if (!this.container) return;
    
    this.track = this.slider.querySelector('.hero-slider__track');
    this.slides = this.slider.querySelectorAll('.hero-slider__slide');
    this.dots = this.slider.querySelectorAll('.hero-slider__dot');
    this.prevBtn = this.slider.querySelector('.hero-slider__nav--prev');
    this.nextBtn = this.slider.querySelector('.hero-slider__nav--next');
    
    // State
    this.currentSlide = 0;
    this.totalSlides = this.slides.length;
    this.autoplaySpeed = parseInt(this.container.dataset.autoplay) * 1000 || 0;
    this.autoplayTimer = null;
    this.isTransitioning = false;
    this.touchStartX = 0;
    this.touchEndX = 0;
    
    this.init();
  }

  init() {
    if (this.totalSlides <= 0) return;
    
    if (this.totalSlides <= 1) {
      this.hideNavigation();
      this.initAnimations();
      return;
    }

    this.setupEventListeners();
    this.setupAnimationIntegration();
    this.initAnimations();
    
    if (this.autoplaySpeed > 0) {
      this.startAutoplay();
    }
    
    // Initialize first slide
    this.updateSlider(0, false);
  }

  setupEventListeners() {
    // Navigation buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prevSlide());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextSlide());
    }
    
    // Dots navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    // Touch/swipe support for mobile
    this.setupTouchEvents();

    // Pause autoplay on hover/focus
    this.slider.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.slider.addEventListener('mouseleave', () => this.resumeAutoplay());
    this.slider.addEventListener('focusin', () => this.pauseAutoplay());
    this.slider.addEventListener('focusout', () => this.resumeAutoplay());
    
    // Keyboard navigation
    this.slider.addEventListener('keydown', (e) => this.handleKeyboard(e));
    
    // Handle visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAutoplay();
      } else if (this.autoplaySpeed > 0) {
        this.resumeAutoplay();
      }
    });
  }

  setupTouchEvents() {
    let touchStartY = 0;
    let isVerticalScroll = false;
    
    this.track.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
      isVerticalScroll = false;
      this.pauseAutoplay();
    }, { passive: true });
    
    this.track.addEventListener('touchmove', (e) => {
      const touchMoveX = e.changedTouches[0].screenX;
      const touchMoveY = e.changedTouches[0].screenY;
      
      // Determine if this is a vertical scroll
      if (!isVerticalScroll && Math.abs(touchMoveY - touchStartY) > Math.abs(touchMoveX - this.touchStartX)) {
        isVerticalScroll = true;
      }
    }, { passive: true });
    
    this.track.addEventListener('touchend', (e) => {
      if (isVerticalScroll) {
        this.resumeAutoplay();
        return;
      }
      
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
      this.resumeAutoplay();
    }, { passive: true });
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;
    
    if (Math.abs(diff) < swipeThreshold) return;
    
    if (diff > 0) {
      // Swiped left
      this.nextSlide();
    } else {
      // Swiped right
      this.prevSlide();
    }
  }

  handleKeyboard(e) {
    switch(e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.prevSlide();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.nextSlide();
        break;
      case ' ':
      case 'Enter':
        if (e.target.classList.contains('hero-slider__dot')) {
          e.preventDefault();
          const index = Array.from(this.dots).indexOf(e.target);
          if (index !== -1) this.goToSlide(index);
        }
        break;
    }
  }

  goToSlide(index) {
    if (index < 0 || index >= this.totalSlides || index === this.currentSlide || this.isTransitioning) {
      return;
    }
    
    this.isTransitioning = true;
    const prevIndex = this.currentSlide;
    this.currentSlide = index;
    this.updateSlider(index);
    
    // Reset transition flag after animation
    setTimeout(() => {
      this.isTransitioning = false;
    }, 600);
    
    // Trigger slide change event
    this.slider.dispatchEvent(new CustomEvent('slidechange', {
      detail: { from: prevIndex, to: index }
    }));
  }

  nextSlide() {
    const nextIndex = (this.currentSlide + 1) % this.totalSlides;
    this.goToSlide(nextIndex);
  }

  prevSlide() {
    const prevIndex = (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
    this.goToSlide(prevIndex);
  }

  updateSlider(index, animate = true) {
    // Update slide visibility with fade
    this.slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('active', isActive);
      
      // Update ARIA attributes for accessibility
      slide.setAttribute('aria-hidden', !isActive);
      
      // Reinitialize animations for the active slide
      if (isActive && animate) {
        this.triggerSlideAnimations(slide);
      }
    });
    
    // Update dots
    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('active', isActive);
      dot.setAttribute('aria-pressed', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
    
    // Update live region for screen readers
    this.updateLiveRegion(index);
  }

  triggerSlideAnimations(slide) {
    // Remove and re-add animation classes to retrigger
    const animatedElements = slide.querySelectorAll('.hero-slider__text, .hero-slider__btn, .hero-slider__secondary-image');
    animatedElements.forEach(el => {
      el.style.animation = 'none';
      el.offsetHeight; // Trigger reflow
      el.style.animation = '';
    });
  }

  updateLiveRegion(index) {
    // Create or update live region for accessibility
    let liveRegion = this.slider.querySelector('.hero-slider__live-region');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'hero-slider__live-region';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.position = 'absolute';
      liveRegion.style.left = '-10000px';
      liveRegion.style.width = '1px';
      liveRegion.style.height = '1px';
      liveRegion.style.overflow = 'hidden';
      this.slider.appendChild(liveRegion);
    }
    
    liveRegion.textContent = `Slide ${index + 1} of ${this.totalSlides}`;
  }

  startAutoplay() {
    if (this.autoplaySpeed <= 0 || this.totalSlides <= 1) return;
    
    this.autoplayTimer = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }

  pauseAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resumeAutoplay() {
    if (this.autoplaySpeed > 0 && !this.autoplayTimer && this.totalSlides > 1) {
      this.startAutoplay();
    }
  }

  hideNavigation() {
    if (this.prevBtn) this.prevBtn.style.display = 'none';
    if (this.nextBtn) this.nextBtn.style.display = 'none';
    if (this.dots.length) {
      const dotsContainer = this.dots[0].parentElement;
      if (dotsContainer) dotsContainer.style.display = 'none';
    }
  }

  setupAnimationIntegration() {
    // Integrate with theme's section-animate system
    const sectionAnimate = this.slider.closest('section-animate');
    if (!sectionAnimate) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          sectionAnimate.setAttribute('data-animate', 'true');
          this.slider.setAttribute('data-animate', 'true');
          this.initAnimations();
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px'
    });
    
    observer.observe(sectionAnimate);
  }

  initAnimations() {
    // Initialize animations for the first/active slide
    requestAnimationFrame(() => {
      this.slider.setAttribute('data-animate', 'true');
      
      // Trigger animations for active slide
      const activeSlide = this.slides[this.currentSlide];
      if (activeSlide) {
        this.triggerSlideAnimations(activeSlide);
      }
    });
  }
  
  destroy() {
    // Clean up event listeners and timers
    this.pauseAutoplay();
    
    if (this.prevBtn) {
      this.prevBtn.removeEventListener('click', () => this.prevSlide());
    }
    
    if (this.nextBtn) {
      this.nextBtn.removeEventListener('click', () => this.nextSlide());
    }
    
    // Remove live region
    const liveRegion = this.slider.querySelector('.hero-slider__live-region');
    if (liveRegion) {
      liveRegion.remove();
    }
  }
}

// Auto-initialize sliders when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSliders);
} else {
  initializeSliders();
}

function initializeSliders() {
  const sliders = document.querySelectorAll('.hero-slider');
  sliders.forEach(slider => {
    // Check if slider is already initialized
    if (!slider.heroSliderInstance) {
      slider.heroSliderInstance = new HeroSlider(slider);
    }
  });
}

// Re-initialize on theme editor changes
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const slider = event.target.querySelector('.hero-slider');
    if (slider) {
      // Destroy existing instance if present
      if (slider.heroSliderInstance) {
        slider.heroSliderInstance.destroy();
      }
      slider.heroSliderInstance = new HeroSlider(slider);
    }
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    const slider = event.target.querySelector('.hero-slider');
    if (slider && slider.heroSliderInstance) {
      slider.heroSliderInstance.destroy();
      delete slider.heroSliderInstance;
    }
  });
  
  document.addEventListener('shopify:block:select', (event) => {
    const slider = event.target.closest('.hero-slider');
    if (slider && slider.heroSliderInstance) {
      const blockIndex = Array.from(slider.querySelectorAll('.hero-slider__slide')).indexOf(event.target);
      if (blockIndex !== -1) {
        slider.heroSliderInstance.pauseAutoplay();
        slider.heroSliderInstance.goToSlide(blockIndex);
      }
    }
  });
  
  document.addEventListener('shopify:block:deselect', (event) => {
    const slider = event.target.closest('.hero-slider');
    if (slider && slider.heroSliderInstance) {
      slider.heroSliderInstance.resumeAutoplay();
    }
  });
}